// src/components/StudentSpreadsheetView.jsx
// A student's marked spreadsheet(s), opened from their dashboard
// (StudentDashboard.jsx's "Marked Spreadsheet" button on a marked
// submission). The same grid the marker marked on (SpreadsheetCellViewer,
// read-only), one button per spreadsheet, the comments and professional
// mark reasons listed underneath, and Print (the marked spreadsheet pages as
// a PDF) and Excel download. The marking comes from the submission's
// `markedSpreadsheet` - see utils/studentSpreadsheetMarking.js.

import React, { useMemo, useState } from 'react';
import { X, Printer, Download, Table2, ChevronRight, ChevronLeft, ChevronDown, ChevronUp, MessageSquare } from 'lucide-react';
import SpreadsheetCellViewer from './SpreadsheetCellViewer';
import { listSpreadsheets, spreadsheetTitle, markedSpreadsheetPdf, downloadMarkedSpreadsheetExcel } from '../utils/studentSpreadsheetMarking';
import { colLetter } from './ExamSpreadsheetGrid';
import useIsMobile from '../hooks/useIsMobile';

const NO_SURPLUS = new Set();
const noop = () => {};

const buttonStyle = (filled) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 14px',
  background: filled ? '#c33329' : 'white',
  color: filled ? 'white' : '#c33329',
  border: '1px solid #c33329',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: '600',
  cursor: 'pointer'
});

export default function StudentSpreadsheetView({ submission, homeworkName, onClose }) {
  const isMobile = useIsMobile();
  const marking = submission.markedSpreadsheet;
  const sheets = useMemo(() => listSpreadsheets(submission.spreadsheetAnswers), [submission.spreadsheetAnswers]);
  const [current, setCurrent] = useState(0);
  const [busy, setBusy] = useState(null); // 'print' | 'excel'
  const [error, setError] = useState('');
  const [commentsOpen, setCommentsOpen] = useState(true);
  const sheet = sheets[current];
  const fileBase = `${homeworkName || submission.homeworkType} marked spreadsheet`.replace(/[^a-z0-9 ]+/gi, '');

  const items = useMemo(() => ({
    marks: marking.marks || [],
    numberedFeedback: marking.numberedFeedback || [],
    professionalMarks: marking.professionalMarks || [],
    additionalProfessionalMarks: marking.additionalProfessionalMarks || [],
    annotations: marking.annotations || [],
    textStamps: marking.textStamps || []
  }), [marking]);

  if (!sheet) return null;
  const onThisSheet = (item) => item.cell.groupId === sheet.groupId && item.cell.gridKey === sheet.gridKey;
  const cellName = (cell) => `${colLetter(cell.col)}${cell.row + 1}`;
  const comments = items.numberedFeedback.filter(onThisSheet).sort((a, b) => a.number - b.number);
  const professional = [...items.professionalMarks, ...items.additionalProfessionalMarks]
    .filter(m => onThisSheet(m) && m.reason)
    .sort((a, b) => (a.markNumber || 0) - (b.markNumber || 0));

  const print = async () => {
    setError('');
    // Opened straight away, while the click still counts as the student's
    // own action, or the browser may block it as a popup
    const win = window.open('', '_blank');
    setBusy('print');
    try {
      const bytes = await markedSpreadsheetPdf(submission.spreadsheetAnswers, marking);
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      if (win) {
        win.location.href = url;
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = `${fileBase}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      console.error('Marked spreadsheet PDF error:', e);
      if (win) win.close();
      setError('Sorry, the spreadsheet could not be prepared for printing. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const downloadExcel = async () => {
    setError('');
    setBusy('excel');
    try {
      await downloadMarkedSpreadsheetExcel(submission.spreadsheetAnswers, marking, `${fileBase}.xlsx`);
    } catch (e) {
      console.error('Marked spreadsheet Excel error:', e);
      setError('Sorry, the Excel file could not be made. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const commentCount = comments.length + professional.length;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, overflowY: 'auto', padding: isMobile ? '0' : '12px' }}>
      <div style={{ background: 'white', borderRadius: isMobile ? 0 : '10px', padding: isMobile ? '16px' : '16px 20px', minHeight: '100%', boxSizing: 'border-box' }}>
        {/* Title, Print/Excel and close on one row, leaving the height for the grid */}
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
          <div style={{ marginRight: 'auto' }}>
            <h2 style={{ margin: 0, fontSize: isMobile ? '18px' : '20px', color: '#2c3e50' }}>Your marked spreadsheet</h2>
            <div style={{ color: '#7f8c8d', fontSize: '13px', marginTop: '2px' }}>{homeworkName || submission.homeworkType}</div>
          </div>
          <button onClick={print} disabled={!!busy} style={{ ...buttonStyle(false), opacity: busy && busy !== 'print' ? 0.5 : 1 }}>
            <Printer size={14} />
            {busy === 'print' ? 'Preparing...' : 'Print'}
          </button>
          <button onClick={downloadExcel} disabled={!!busy} style={{ ...buttonStyle(false), opacity: busy && busy !== 'excel' ? 0.5 : 1 }}>
            <Download size={14} />
            {busy === 'excel' ? 'Preparing...' : 'Download as Excel'}
          </button>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4a5568', padding: '4px' }}>
            <X size={24} />
          </button>
        </div>
        {error && <div style={{ color: '#c0392b', fontSize: '14px', marginBottom: '12px' }}>{error}</div>}

        {sheets.length > 1 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
            {sheets.map((s, i) => (
              <button key={`${s.groupId}:${s.gridKey}`} onClick={() => setCurrent(i)} style={buttonStyle(i === current)}>
                <Table2 size={14} />
                {spreadsheetTitle(s, i)}
              </button>
            ))}
          </div>
        )}
        {sheets.length === 1 && (
          <div style={{ fontSize: '15px', fontWeight: '600', color: '#2c3e50', marginBottom: '12px' }}>{spreadsheetTitle(sheet, 0)}</div>
        )}

        {/* Comments in a margin to the right of the grid, scrolling on their
            own when there are a lot of them - underneath on a phone */}
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '16px', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0, width: isMobile ? '100%' : 'auto' }}>
        <SpreadsheetCellViewer
          key={`${sheet.groupId}:${sheet.gridKey}`}
          groupId={sheet.groupId}
          gridKey={sheet.gridKey}
          answer={submission.spreadsheetAnswers[sheet.groupId]}
          fluid
          readOnly
          items={items}
          activeTool="view"
          surplusIds={NO_SURPLUS}
          onCellClick={noop}
          onHighlightRange={noop}
          onDeleteProfessionalMark={noop}
          colWidthOverrides={marking.spreadsheetColWidths}
          onColWidthsChange={noop}
          gridMaxHeight={isMobile ? '70vh' : 'calc(100vh - 260px)'}
        />
        </div>

        {/* Click the heading to fold the comments away (a thin strip on a
            computer, just the heading on a phone) and again to open them */}
        {!isMobile && !commentsOpen ? (
          <button
            onClick={() => setCommentsOpen(true)}
            title="Show comments"
            style={{
              marginTop: '46px', width: '36px', flexShrink: 0, alignSelf: 'stretch', maxHeight: 'calc(100vh - 260px)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '10px 0',
              background: '#f8f9fa', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', color: '#2c3e50'
            }}
          >
            <ChevronLeft size={16} />
            <MessageSquare size={16} />
            <span style={{ writingMode: 'vertical-rl', fontSize: '13px', fontWeight: '600' }}>Comments ({commentCount})</span>
          </button>
        ) : (
        <div style={{
          width: isMobile ? '100%' : '320px',
          flexShrink: 0,
          // Lined up with the grid (below the formula bar), as tall as it
          marginTop: isMobile ? 0 : '46px',
          maxHeight: isMobile ? 'none' : 'calc(100vh - 260px)',
          overflowY: isMobile ? 'visible' : 'auto',
          borderLeft: isMobile ? 'none' : '2px solid #e2e8f0',
          paddingLeft: isMobile ? 0 : '14px'
        }}>
          <button
            onClick={() => setCommentsOpen(open => !open)}
            title={commentsOpen ? 'Hide comments' : 'Show comments'}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%', background: 'none', border: 'none', padding: '0 0 6px', cursor: 'pointer', color: '#2c3e50', fontSize: '15px', fontWeight: '600', textAlign: 'left' }}
          >
            <MessageSquare size={16} />
            <span style={{ flex: 1 }}>Comments ({commentCount})</span>
            {isMobile ? (commentsOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />) : <ChevronRight size={16} />}
          </button>
          {commentsOpen && (commentCount > 0 ? (
          <div>
            {comments.map(f => (
              <div key={f.id} style={{ display: 'flex', gap: '10px', padding: '8px 0', borderBottom: '1px solid #edf2f7' }}>
                <span style={{ color: '#e74c3c', fontWeight: 'bold', fontSize: '16px', minWidth: '22px' }}>{f.number}</span>
                <div style={{ fontSize: '14px', color: '#2d3748' }}>
                  <span style={{ color: '#7f8c8d', marginRight: '6px' }}>{cellName(f.cell)}</span>
                  {f.text}
                </div>
              </div>
            ))}
            {professional.map(m => (
              <div key={m.id} style={{ display: 'flex', gap: '10px', padding: '8px 0', borderBottom: '1px solid #edf2f7' }}>
                <span style={{ color: '#f39c12', fontWeight: 'bold', fontSize: '14px', minWidth: '22px' }}>P{m.markNumber || ''}</span>
                <div style={{ fontSize: '14px', color: '#2d3748' }}>
                  <span style={{ color: '#7f8c8d', marginRight: '6px' }}>{cellName(m.cell)}</span>
                  {m.reason}
                </div>
              </div>
            ))}
          </div>
          ) : (
            <div style={{ fontSize: '14px', color: '#7f8c8d' }}>No comments on this spreadsheet.</div>
          ))}
        </div>
        )}
        </div>
        <div style={{ fontSize: '12px', color: '#95a5a6', marginTop: '14px' }}>
          Point at a cell to see its formula. The full feedback on your homework is in View Feedback.
        </div>
      </div>
    </div>
  );
}
